import { useCallback, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Tracks a text selection inside a plain `<textarea>`/`<input>` so a floating
 * toolbar can act on it (Explain/Improve/Shorten/Fix grammar/Change tone).
 *
 * Deliberately native-selection, not `window.getSelection()` — a form
 * control's internal text selection is invisible to the Selection API; the
 * only way to read it is `element.selectionStart`/`selectionEnd` on the
 * element itself. This also means positioning is anchored to the FIELD, not
 * to the precise pixel location of the substring — getting a native
 * textarea's per-character caret position requires a hidden "mirror div"
 * measurement technique, which is real complexity for a v1 that a handful of
 * textarea/input surfaces don't need yet. "Toolbar appears near the field
 * you're editing" is the honest, shippable scope; pixel-precise anchoring is
 * a separate, later upgrade if a surface actually needs it.
 *
 * NOT for the Home composer — that's Tiptap/contentEditable, a different
 * selection model entirely, and out of scope for this pass (see
 * TextSelectionToolbar's own doc comment for why).
 */
export interface TextSelectionState {
  start: number;
  end: number;
  text: string;
}

export interface UseTextSelectionActionsResult {
  /**
   * Spread onto the same element the caller passed as `fieldRef` — composes
   * with whatever `onSelect`/`onMouseUp`/`onKeyUp`/`onBlur` the caller
   * already has (call both in sequence) rather than owning the element.
   */
  fieldHandlers: {
    onSelect: () => void;
    onMouseUp: () => void;
    onKeyUp: () => void;
    onBlur: (event?: { relatedTarget?: EventTarget | null }) => void;
  };
  /** Non-null exactly when there's a live, non-empty selection. */
  selection: TextSelectionState | null;
  /** Splices `newText` over the tracked selection and clears it. */
  applySelectionResult: (newText: string) => void;
  /** Clears the tracked selection without changing the field's value. */
  dismissSelection: () => void;
}

/**
 * @param fieldRef The textarea/input's own ref, owned by the caller — this
 *   hook never creates its own, so it composes with a surface that already
 *   holds a ref on the same element for something else (e.g. auto-resize).
 */
export function useTextSelectionActions(
  fieldRef: RefObject<HTMLTextAreaElement | HTMLInputElement>,
  value: string,
  onValueChange: (next: string) => void,
): UseTextSelectionActionsResult {
  const [selection, setSelection] = useState<TextSelectionState | null>(null);

  const updateSelection = useCallback(() => {
    const el = fieldRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (end - start < 1) {
      setSelection(null);
      return;
    }
    setSelection({ start, end, text: value.slice(start, end) });
  }, [value, fieldRef]);

  // A toolbar button's own onMouseDown calls event.preventDefault(), which
  // stops the browser from ever moving focus off the field on click — so for
  // every BUTTON in the toolbar this never fires at all.
  //
  // The one control that must genuinely take focus is the toolbar's freeform
  // "Describe edits" field, and without this guard clicking into it destroyed
  // the very selection it was about to edit: blur → setSelection(null) → the
  // toolbar unmounts mid-click. `[data-selection-toolbar]` is set by
  // `TextSelectionToolbar` on its portal root, so focus moving anywhere inside
  // the toolbar reads as still-editing rather than as walking away.
  const onBlur = useCallback((event?: { relatedTarget?: EventTarget | null }) => {
    const next = event?.relatedTarget;
    if (next instanceof Element && next.closest('[data-selection-toolbar]')) return;
    setSelection(null);
  }, []);

  // NOT a functional setSelection(updater) — calling onValueChange (an
  // ancestor's setState) from inside another component's state-updater
  // function is exactly the "Cannot update a component while rendering a
  // different component" anti-pattern React warns about (updaters must be
  // pure; React may invoke them outside a normal event-handler context).
  // `selection` from the closure is current here regardless, since this only
  // ever runs from a toolbar button's onClick after the selection already
  // rendered.
  const applySelectionResult = useCallback(
    (newText: string) => {
      if (!selection) return;
      const next = value.slice(0, selection.start) + newText + value.slice(selection.end);
      onValueChange(next);
      setSelection(null);
    },
    [selection, value, onValueChange],
  );

  const dismissSelection = useCallback(() => setSelection(null), []);

  return {
    fieldHandlers: {
      onSelect: updateSelection,
      onMouseUp: updateSelection,
      onKeyUp: updateSelection,
      onBlur,
    },
    selection,
    applySelectionResult,
    dismissSelection,
  };
}
